import React, { useEffect, useState } from "react";
import { ReactFlow, Background, Panel, Controls } from '@xyflow/react';
import NodeComponent from "./NodeComponent";
import StaticEdgeComponent from "./StaticEdgeComponent";
import { EDGE_STATUS, isInstanceOfEdge } from "./workflowConstants";

import '@xyflow/react/dist/style.css';

// `variant="instance"` switches NodeComponent to status colors (no tooltip/borders)
const InstanceNodeComponent = (props) => <NodeComponent {...props} variant="instance" />;

const nodeTypes = {
    'inputNode': InstanceNodeComponent,
    'outputNode': InstanceNodeComponent,
    'completedNode': InstanceNodeComponent,
    'activeNode': InstanceNodeComponent,
    'upcomingNode': InstanceNodeComponent,
    'completedDecisionNode': InstanceNodeComponent,
    'activeDecisionNode': InstanceNodeComponent,
    'upcomingDecisionNode': InstanceNodeComponent,
    'completedApprovalNode': InstanceNodeComponent,
    'activeApprovalNode': InstanceNodeComponent,
    'upcomingApprovalNode': InstanceNodeComponent
};
const edgeTypes = {
    'completedEdge': StaticEdgeComponent,
    'activeEdge': StaticEdgeComponent,
    'upcomingEdge': StaticEdgeComponent,
    'blockedEdge': StaticEdgeComponent
};

export default function WorkflowStateComponent(props) {
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);

    useEffect(() => {
        if (props.workflowState) {
            getNodesAndEdges();
        }
    }, [props.workflowState]);

    const getNodesAndEdges = () => {
        const { wf_nodes, wf_edges, nodeData, edgeData } = props.workflowState;

        const parsedNodes = JSON.parse(wf_nodes);
        const parsedEdges = JSON.parse(wf_edges);

        // The latest record for a definition edge — an edge on a loop is traversed more than once
        const getEdgeRecord = (edge) => (edgeData || []).filter(e => isInstanceOfEdge(e, edge)).pop();

        // Find the active and blocked edges
        const activeEdgeRecord = parsedEdges.map(getEdgeRecord).find(e => e?.edge_status === EDGE_STATUS.ACTIVE) || null;
        const blockedEdgeRecord = parsedEdges.map(getEdgeRecord).find(e => e?.edge_status === EDGE_STATUS.BLOCKED) || null;

        // Node types
        const nodeTypes = parsedNodes.map(node => {
            if (node.type === 'inputNode' || node.type === 'outputNode') {
                return { ...node };
            }

            if (node.type === 'decisionNode' || node.type === 'approvalNode') {
                const kind = node.type === 'decisionNode' ? 'Decision' : 'Approval';
                const nodeDetails = nodeData?.find(n => n.node_wf_nodeID === node.id);
                const type = nodeDetails
                    ? nodeDetails.node_status === 0
                        ? `completed${kind}Node`
                        : `active${kind}Node`
                    : `upcoming${kind}Node`;

                return {
                    ...node,
                    type,
                    data: {
                        ...node.data,
                        entityData: {
                            ...nodeDetails,
                            ...node,
                            entityType: 'node'
                        }
                    }
                };
            }

            const nodeDetails = nodeData?.find(n => n.node_wf_nodeID === node.id);
            const type = nodeDetails
                ? nodeDetails.node_status === 0
                    ? 'completedNode'
                    : 'activeNode'
                : 'upcomingNode';

            return {
                ...node,
                type,
                data: {
                    ...node.data,
                    entityData: {
                        ...nodeDetails,
                        ...node,
                        entityType: 'node'
                    }
                }
            };
        });

        // Edge Types
        const edgeTypes = parsedEdges.map(edge => {
            const edgeDetails = getEdgeRecord(edge);

            const type = edgeDetails?.edge_status === EDGE_STATUS.COMPLETED
                ? 'completedEdge'
                : edgeDetails && edgeDetails === blockedEdgeRecord
                    ? 'blockedEdge'
                    : edgeDetails && edgeDetails === activeEdgeRecord
                        ? 'activeEdge'
                        : 'upcomingEdge';

            return {
                ...edge,
                animated: type !== 'completedEdge' && type !== 'blockedEdge',
                type,
                data: {
                    ...edge.data,
                    entityData: {
                        ...edgeDetails,
                        ...edge,
                        entityType: 'edge'
                    }
                }
            };
        });

        setNodes(nodeTypes);
        setEdges(edgeTypes);
    };

    const PanelComponent = () => {
        return (
            <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', boxShadow: '0 0 6px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, background: 'lightgray', borderRadius: 3 }}></div>
                    <span style={{ fontSize: 10 }}>Start / End</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, background: 'gray', borderRadius: 3 }}></div>
                    <span style={{ fontSize: 10 }}>Completed</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, background: 'lightgreen', borderRadius: 3 }}></div>
                    <span style={{ fontSize: 10 }}>Active</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, background: 'orange', borderRadius: 3 }}></div>
                    <span style={{ fontSize: 10 }}>Upcoming</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 10, height: 10, background: '#d32f2f', borderRadius: 3 }}></div>
                    <span style={{ fontSize: 10 }}>Blocked</span>
                </div>
            </div>
        );
    };

    return (
        <div style={{ height: '100%' }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                elementsSelectable={false}
                onNodeClick={(event, node) => {
                    props.onClick(node?.data?.entityData);
                }}
                onEdgeClick={(event, edge) => {
                    props.onClick(edge?.data?.entityData);
                }}
            >
                <Panel position="top-right">{PanelComponent()}</Panel>
                <Controls position="bottom-right" showInteractive={false} />
                <Background variant="dots" gap={12} size={1} />
            </ReactFlow>
        </div>
    );
};